import { Navigate, Route, Routes } from "react-router-dom";
import AccountingShell from "./AccountingShell";
import AccountingDashboardPage from "./AccountingDashboardPage";
import ChartOfAccountsPage from "./ChartOfAccountsPage";
import AccountGroupViewPage from "./AccountGroupViewPage";
import LedgersPage from "./LedgersPage";
import LedgerViewPage from "./LedgerViewPage";
import LedgerEditPage from "./LedgerEditPage";
import VendorsMasterPage from "./VendorsMasterPage";
import VendorViewPage from "./VendorViewPage";
import VendorEditPage from "./VendorEditPage";
import BankAccountsPage from "./BankAccountsPage";
import BankAccountViewPage from "./BankAccountViewPage";
import BankAccountEditPage from "./BankAccountEditPage";
import FundsPage from "./FundsPage";
import FundViewPage from "./FundViewPage";
import FundEditPage from "./FundEditPage";
import FixedDepositsPage from "./FixedDepositsPage";
import FixedDepositViewPage from "./FixedDepositViewPage";
import FixedDepositEditPage from "./FixedDepositEditPage";
import FinancialYearsPage from "./FinancialYearsPage";
import FinancialYearViewPage from "./FinancialYearViewPage";
import FinancialYearClosingPage from "./FinancialYearClosingPage";
import VouchersPage from "./VouchersPage";
import VoucherViewPage from "./VoucherViewPage";
import VoucherEditPage from "./VoucherEditPage";
import ReceiptVoucherPage from "./ReceiptVoucherPage";
import PaymentVoucherPage from "./PaymentVoucherPage";
import ContraVoucherPage from "./ContraVoucherPage";
import JournalVoucherPage from "./JournalVoucherPage";
import VendorBillsPage from "./VendorBillsPage";
import CreditNotePage from "./CreditNotePage";
import DebitNotePage from "./DebitNotePage";
import MemberReceivablesPage from "./MemberReceivablesPage";
import MemberAdvancesPage from "./MemberAdvancesPage";
import QuickMemberReceiptsPage from "./QuickMemberReceiptsPage";
import MemberLedgerPage from "./MemberLedgerPage";
import VendorPayablesPage from "./VendorPayablesPage";
import VendorLedgerPage from "./VendorLedgerPage";
import BankReconciliationPage from "./BankReconciliationPage";
import BankReconciliationWorkspacePage from "./BankReconciliationWorkspacePage";
import BankReconciliationManualPage from "./BankReconciliationManualPage";
import AccountingMigrationPage from "./AccountingMigrationPage";
import { ACCOUNTING_BASE } from "./accountingNav";
import TrialBalancePage from "./reports/TrialBalancePage";
import GeneralLedgerPage from "./reports/GeneralLedgerPage";
import DayBookPage from "./reports/DayBookPage";
import CashBookPage from "./reports/CashBookPage";
import BankBookPage from "./reports/BankBookPage";
import IncomeExpenditurePage from "./reports/IncomeExpenditurePage";
import BalanceSheetPage from "./reports/BalanceSheetPage";
import MemberReceivablesReportPage from "./reports/MemberReceivablesReportPage";
import VendorPayablesReportPage from "./reports/VendorPayablesReportPage";
import VendorBillsOutstandingPage from "./reports/VendorBillsOutstandingPage";
import FundPositionPage from "./reports/FundPositionPage";
import MonthlyCommitteeAccountsPage from "./reports/MonthlyCommitteeAccountsPage";
import AuditTrailPage from "./AuditTrailPage";
import AuditorsPage from "./AuditorsPage";
import AccountingSettingsPage from "./AccountingSettingsPage";
import VoucherNumberingSettingsPage from "./VoucherNumberingSettingsPage";
import BillingLedgerMappingPage from "./BillingLedgerMappingPage";

function AccountingNotFound() {
  return (
    <div className="rounded-sm border border-[#E5E7EB] bg-white p-10 text-center">
      <div className="text-sm font-extrabold uppercase tracking-[0.16em] text-[#6B7280]">Page not found</div>
      <p className="mt-2 text-sm text-[#6B7280]">This Accounting page does not exist.</p>
    </div>
  );
}

/**
 * Mounted at /client/society/accounting/* in App.js. Owns its own nested
 * <Routes> (same pattern as pages/store/PublicStore.jsx) so every Accounting
 * destination gets a real URL, browser refresh/back/forward all work, and
 * the old single giant Accounting page is no longer the architecture.
 *
 * Every route from the original Phase 2 pass is kept as-is (URLs are not
 * removed/renamed) per the Phase 2 navigation-correction instructions; this
 * revision only adds new routes for the expanded nav (Vendors master, Funds,
 * Fixed Deposits, Credit/Debit Note, Member Advances/Ledger, Vendor Ledger,
 * Receivables/Payables Aging, Fund Position) and reorganizes how
 * AccountingShell/accountingNav present them. reports/receivables and
 * reports/payables are intentionally left mounted (unlinked from the new
 * menu, which now points Members/Vendors at their own outstanding pages and
 * Reports at the new aging pages instead) rather than deleted.
 */
export default function AccountingLayout() {
  return (
    <Routes>
      <Route element={<AccountingShell />}>
        <Route index element={<Navigate to="/client/society/accounting/dashboard" replace />} />
        <Route path="dashboard" element={<AccountingDashboardPage />} />

        <Route path="chart-of-accounts" element={<ChartOfAccountsPage />} />
        <Route path="chart-of-accounts/:groupId" element={<AccountGroupViewPage />} />
        <Route path="ledgers" element={<LedgersPage />} />
        <Route path="ledgers/:id" element={<LedgerViewPage />} />
        <Route path="ledgers/:id/edit" element={<LedgerEditPage />} />
        <Route path="vendors" element={<VendorsMasterPage />} />
        <Route path="vendors/:id" element={<VendorViewPage />} />
        <Route path="vendors/:id/edit" element={<VendorEditPage />} />
        <Route path="bank-accounts" element={<BankAccountsPage />} />
        <Route path="bank-accounts/:id" element={<BankAccountViewPage />} />
        <Route path="bank-accounts/:id/edit" element={<BankAccountEditPage />} />
        <Route path="funds" element={<FundsPage />} />
        <Route path="funds/:id" element={<FundViewPage />} />
        <Route path="funds/:id/edit" element={<FundEditPage />} />
        <Route path="fixed-deposits" element={<FixedDepositsPage />} />
        <Route path="fixed-deposits/:id" element={<FixedDepositViewPage />} />
        <Route path="fixed-deposits/:id/edit" element={<FixedDepositEditPage />} />
        <Route path="financial-years" element={<FinancialYearsPage />} />
        <Route path="financial-years/:id" element={<FinancialYearViewPage />} />
        <Route path="financial-year-closing" element={<FinancialYearClosingPage />} />
        <Route path="settings/voucher-numbering" element={<VoucherNumberingSettingsPage />} />
        <Route path="settings/billing-mapping" element={<BillingLedgerMappingPage />} />

        <Route path="vouchers" element={<VouchersPage />} />
        <Route path="vouchers/:id" element={<VoucherViewPage />} />
        <Route path="vouchers/:id/edit" element={<VoucherEditPage />} />
        <Route path="receipts" element={<ReceiptVoucherPage />} />
        <Route path="payments" element={<PaymentVoucherPage />} />
        <Route path="contra" element={<ContraVoucherPage />} />
        <Route path="journals" element={<JournalVoucherPage />} />
        <Route path="vendor-bills" element={<VendorBillsPage />} />
        <Route path="credit-notes" element={<CreditNotePage />} />
        <Route path="debit-notes" element={<DebitNotePage />} />

        <Route path="member-receivables" element={<MemberReceivablesPage />} />
        <Route path="member-advances" element={<MemberAdvancesPage />} />
        <Route path="quick-member-receipts" element={<QuickMemberReceiptsPage />} />
        <Route path="member-ledger" element={<MemberLedgerPage />} />

        <Route path="vendor-payables" element={<VendorPayablesPage />} />
        <Route path="vendor-ledger" element={<VendorLedgerPage />} />

        {/* Phase 9: the real, engine-backed reconciliation workspace now
            lives at this spec-mandated path (scoped to one real Accounting
            Bank Account at a time). The legacy Phase 1/2 page (free-text
            "account" string, matched only against Billing payments) is kept
            mounted at -legacy, never deleted. */}
        {/* Phase 16: the manual per-voucher "tick as cleared" flow is now the
            primary Bank Reconciliation experience (this is the page the
            Phase 16 spec describes end to end). The Phase 9 statement-
            import/auto-match workspace is NOT redesigned or removed - its
            own code is untouched - it is only moved to a new route and
            demoted in the nav menu, the exact same non-invasive pattern
            already used for the original Phase 1/2 CSV page below. */}
        <Route path="bank-reconciliation" element={<BankReconciliationManualPage />} />
        <Route path="bank-reconciliation-statement-import" element={<BankReconciliationWorkspacePage />} />
        <Route path="bank-reconciliation-legacy" element={<BankReconciliationPage />} />

        {/* Phase 11: the central Opening Balance migration workflow (multi-tab
            batch/confirm) is retired - opening balances are entered directly
            on each master's own Create/Edit form instead (see Bank Account,
            Ledger, Vendor, Fund, Fixed Deposit forms, and the Member
            Receivables row action). This route now redirects rather than
            rendering the old batch UI, so no stale bookmark/link can reach
            it; the backend records and GET/history endpoints behind it are
            left untouched for any already-posted historical batch. */}
        <Route path="opening-balances" element={<Navigate to={`${ACCOUNTING_BASE}/dashboard`} replace />} />
        <Route path="opening-balances/*" element={<Navigate to={`${ACCOUNTING_BASE}/dashboard`} replace />} />
        {/* Migration / Go-Live Settings is NOT part of the forbidden central
            opening-balance workflow - it only stores a Financial Year /
            Go-Live Date / Previous Software note that gates when Billing
            stops auto-posting historical bills into Accounting. Kept
            mounted and restored to nav (renamed "Go-Live Settings" to avoid
            the retired "Migration" terminology) - see accountingNav.js. */}
        <Route path="migration" element={<AccountingMigrationPage />} />

        <Route path="reports/trial-balance" element={<TrialBalancePage />} />
        <Route path="reports/general-ledger" element={<GeneralLedgerPage />} />
        <Route path="reports/day-book" element={<DayBookPage />} />
        <Route path="reports/cash-book" element={<CashBookPage />} />
        <Route path="reports/bank-book" element={<BankBookPage />} />
        <Route path="reports/income-expenditure" element={<IncomeExpenditurePage />} />
        <Route path="reports/balance-sheet" element={<BalanceSheetPage />} />
        <Route path="reports/fund-position" element={<FundPositionPage />} />
        <Route path="reports/monthly-committee-accounts" element={<MonthlyCommitteeAccountsPage />} />
        {/* Phase 8: the real Member Receivables / Vendor Payables Reports now
            live at exactly these spec-mandated paths. The old "-aging"
            paths from the Phase 2 nav redesign are kept mounted as redirects
            here (never deleted) so any existing bookmark/link still lands
            somewhere real. */}
        <Route path="reports/receivables" element={<MemberReceivablesReportPage />} />
        <Route path="reports/payables" element={<VendorPayablesReportPage />} />
        <Route path="reports/vendor-bills" element={<VendorBillsOutstandingPage />} />
        <Route path="reports/receivables-aging" element={<Navigate to="/client/society/accounting/reports/receivables" replace />} />
        <Route path="reports/payables-aging" element={<Navigate to="/client/society/accounting/reports/payables" replace />} />

        <Route path="audit-trail" element={<AuditTrailPage />} />
        <Route path="auditors" element={<AuditorsPage />} />
        <Route path="settings" element={<AccountingSettingsPage />} />

        <Route path="*" element={<AccountingNotFound />} />
      </Route>
    </Routes>
  );
}
